import { useState, useEffect, useCallback } from "react"
import {
  Alert,
  Box,
  Button,
  Collapse,
  IconButton,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp"
import { Title } from "components/Common/Title"
import analyticsService from "services/analyticsService"
import type { IAnalyticsExamRetake, IAnalyticsExamAttempt } from "types/analyticsTypes"

type Props = {
  orgId: string | null
}

type RetakeRowProps = {
  row: IAnalyticsExamRetake
}

function RetakeRow({ row }: RetakeRowProps) {
  const [open, setOpen] = useState(false)

  return (
    <>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen((prev) => !prev)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{row.userName}</TableCell>
        <TableCell>{row.lessonName ?? row.lessonId}</TableCell>
        <TableCell align="right">{row.attemptCount}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={4} sx={{ py: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ m: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Attempts
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Result</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.attempts.map((attempt: IAnalyticsExamAttempt, index: number) => (
                    <TableRow key={`${attempt.date}-${attempt.result}-${index}`}>
                      <TableCell>{new Date(attempt.date).toLocaleDateString()}</TableCell>
                      <TableCell
                        sx={{
                          color: attempt.result === "Pass" ? "success.main" : "error.main",
                          fontWeight: 500,
                        }}
                      >
                        {attempt.result}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

export function ExamRetakeSection({ orgId }: Props) {
  const [data, setData] = useState<IAnalyticsExamRetake[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await analyticsService.getExamRetakes(orgId)
      setData(result)
    } catch {
      setError("Failed to load exam retake data.")
    } finally {
      setLoading(false)
    }
  }, [orgId])

  useEffect(() => {
    load()
  }, [load])

  return (
    <Paper sx={{ p: 3 }}>
      <Title>EXAM RETAKES</Title>
      {loading && (
        <>
          <Skeleton variant="rectangular" width="100%" height={36} sx={{ mb: 1 }} />
          {Array.from({ length: 5 }, (_, i) => `skeleton-row-${i}`).map((key) => (
            <Skeleton key={key} variant="rectangular" width="100%" height={32} sx={{ mb: 0.5 }} />
          ))}
        </>
      )}
      {!loading && error && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={load}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}
      {!loading && !error && data.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No users have retaken an exam.
        </Typography>
      )}
      {!loading && !error && data.length > 0 && (
        <TableContainer sx={{ maxHeight: 500 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>User</TableCell>
                <TableCell>Lesson</TableCell>
                <TableCell align="right">Attempts</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row) => (
                <RetakeRow key={`${row.userId}-${row.lessonId}`} row={row} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  )
}
